import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ChevronRight, FileText, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface DocsPageProps {
  content?: string;
  title?: string;
  lastModified?: string;
}

// Simple markdown to HTML converter for basic formatting
function parseMarkdown(markdown: string): string {
  return markdown
    // Headers
    .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-8 mb-4">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-10 mb-6">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-12 mb-8">$1</h1>')

    // Code blocks
    .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="bg-gray-100 rounded-lg p-4 overflow-x-auto my-4"><code class="text-sm">$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-1 rounded text-sm font-mono">$1</code>')

    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:text-primary/80 underline">$1</a>')

    // Bold and italic
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')

    // Lists
    .replace(/^\- (.+$)/gim, '<li class="ml-4">• $1</li>')
    .replace(/^\d+\. (.+$)/gim, '<li class="ml-4">$1</li>')

    // Paragraphs
    .replace(/\n\n/g, '</p><p class="mb-4">')
    .replace(/^(?!<[h|l|p|d|c|a])(.+$)/gim, '<p class="mb-4">$1</p>');
}

// Map URL paths to markdown file paths
function getMarkdownPath(pathname: string): string {
  // Remove /docs prefix and convert to file path
  const path = pathname.replace('/docs/', '').replace(/\/$/, '');

  if (!path || path === 'docs') {
    return 'README.md';
  }

  // Handle specific mappings
  const pathMappings: Record<string, string> = {
    'api-reference': 'API_REFERENCE.md',
    'chat-architecture': 'CHAT_ARCHITECTURE.md',
    'usage-examples': 'USAGE_EXAMPLES.md',
    'migration-guide': 'MIGRATION_GUIDE.md',
    'performance-guide': 'PERFORMANCE_GUIDE.md',
    'coding-standards': 'CODING_STANDARDS.md',
    'troubleshooting': 'TROUBLESHOOTING.md',
    'getting-started/installation': 'getting-started/INSTALLATION.md',
    'getting-started/creating-first-chatbot': 'getting-started/CREATING_FIRST_CHATBOT.md',
    'project-overview/introduction': 'project-overview/INTRODUCTION.md',
    'project-overview/architecture-overview': 'project-overview/ARCHITECTURE_OVERVIEW.md',
    'technical-architecture/frontend-architecture': 'technical-architecture/FRONTEND_ARCHITECTURE.md',
    'technical-architecture/backend-services': 'technical-architecture/BACKEND_SERVICES.md',
    'integration-guides/embedding-chat-widget': 'integration-guides/EMBEDDING_CHAT_WIDGET.md',
    'integration-guides/webhook-integration': 'integration-guides/WEBHOOK_INTEGRATION.md',
    'integration-guides/pii-integration': 'integration-guides/PII_INTEGRATION.md',
    'advanced-features/ai-response-validation': 'advanced-features/AI_RESPONSE_VALIDATION.md',
    'advanced-features/pii-handling': 'advanced-features/PII_HANDLING.md',
    'advanced-features/streaming-responses': 'advanced-features/STREAMING_RESPONSES.md',
    'advanced-features/webhook-system': 'advanced-features/WEBHOOK_SYSTEM.md',
    'customization/theming-system': 'customization/THEMING_SYSTEM.md',
    'customization/rich-media': 'customization/RICH_MEDIA.md',
    'troubleshooting/common-issues': 'troubleshooting/COMMON_ISSUES.md',
  };

  // Check if we have a direct mapping
  if (pathMappings[path]) {
    return pathMappings[path];
  }

  // If it's a directory, try to get the README
  if (path.endsWith('/')) {
    return `${path}README.md`;
  }

  // Try directory README
  return `${path}/README.md`;
}

export default function DocsPage({ content, title, lastModified }: DocsPageProps) {
  const location = useLocation();
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadMarkdown() {
      try {
        setLoading(true);
        setError(null);

        const markdownPath = getMarkdownPath(location.pathname);

        // Try to fetch the markdown file
        const response = await fetch(`/docs/${markdownPath}`);

        if (!response.ok) {
          throw new Error(`Documentation not found: ${markdownPath}`);
        }

        const text = await response.text();
        setMarkdownContent(text);
      } catch (err) {
        console.error('Error loading documentation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load documentation');
      } finally {
        setLoading(false);
      }
    }

    if (!content) {
      loadMarkdown();
    } else {
      setMarkdownContent(content);
      setLoading(false);
    }
  }, [location.pathname, content]);

  // Generate breadcrumbs from URL
  const breadcrumbs = location.pathname
    .split('/')
    .filter(Boolean)
    .map((segment, index, array) => {
      const path = '/' + array.slice(0, index + 1).join('/');
      const label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      return { label, path };
    });

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>

        <div className="text-center space-y-4">
          <FileText className="h-16 w-16 text-gray-400 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900">Page Not Found</h2>
          <p className="text-gray-600">
            The documentation page you're looking for doesn't exist or has been moved.
          </p>
          <div className="flex justify-center gap-4">
            <Button asChild>
              <Link to="/docs">
                Back to Documentation
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <a
                href="https://github.com/ScaledByDesign/intentdrives/tree/main/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                View on GitHub <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const parsedContent = parseMarkdown(markdownContent);

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      {breadcrumbs.length > 1 && (
        <nav className="flex items-center space-x-1 text-sm text-gray-500">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              {index > 0 && <ChevronRight className="h-4 w-4" />}
              {index === breadcrumbs.length - 1 ? (
                <span className="text-gray-900 font-medium">{crumb.label}</span>
              ) : (
                <Link
                  to={crumb.path}
                  className="hover:text-gray-700 transition-colors"
                >
                  {crumb.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Content */}
      <article className="prose prose-slate max-w-none">
        <div
          dangerouslySetInnerHTML={{ __html: parsedContent }}
          className="markdown-content"
        />
      </article>

      {/* Footer */}
      <footer className="border-t pt-6 mt-12">
        <div className="flex justify-between items-center text-sm text-gray-500">
          <div>
            {lastModified && (
              <span>Last updated: {new Date(lastModified).toLocaleDateString()}</span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" asChild>
              <a
                href={`https://github.com/ScaledByDesign/intentdrives/edit/main/docs/${getMarkdownPath(location.pathname)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                Edit on GitHub <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
